import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import { RiSearch2Fill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { getManualExamMarksRequest } from "../../../modules/admin-modules/reports/Actions";
import { getManualExamsRequest } from "../../../modules/admin-modules/exams/Actions";

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";

const selectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
    minWidth: "260px",
  }),
};

const csvHeaders = [
  { label: "Student name", key: "name" },
  { label: "Code", key: "user_name" },
  { label: "Phone", key: "phone" },
  { label: "Parent phone", key: "parent_phone" },
  { label: "Score", key: "score" },
];

const ManualExamMarksReport = () => {
  const dispatch = useDispatch();
  const [examId, setExamId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const exams = useSelector((state) => state.exams.exams);
  const manualExamMarks = useSelector((state) => state.reports.manualExamMarks);
  const loading = useSelector((state) => state.reports.isLoading);

  useEffect(() => {
    dispatch(getManualExamsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!examId) return;
    dispatch(getManualExamMarksRequest({ exam_id: examId }));
  }, [examId, dispatch]);

  const examOptions = useMemo(() => {
    const raw = Array.isArray(exams) ? exams : [];
    return raw.map((exam) => ({
      value: String(exam.id),
      label: exam.name || `Exam #${exam.id}`,
    }));
  }, [exams]);

  const rows = useMemo(() => {
    const raw = Array.isArray(manualExamMarks) ? manualExamMarks : [];
    const term = searchTerm.trim().toLowerCase();
    return raw
      .filter((item) => {
        if (!term) return true;
        const name = item?.student?.name ?? "";
        const phone = item?.student?.phone ?? "";
        const code = item?.student?.user_name ?? "";
        return (
          name.toLowerCase().includes(term) ||
          phone.toLowerCase().includes(term) ||
          code.toLowerCase().includes(term)
        );
      })
      .map((item, index) => ({
        key: item.student_id ?? index,
        index: index + 1,
        name: item?.student?.name ?? "—",
        user_name: item?.student?.user_name ?? "—",
        phone: item?.student?.phone ?? "—",
        parent_phone: item?.student?.parent_phone ?? "—",
        score:
          item?.score != null && item?.score !== ""
            ? String(item.score)
            : "Not graded",
      }));
  }, [manualExamMarks, searchTerm]);

  const csvData = useMemo(
    () =>
      rows.map((row) => ({
        name: row.name,
        user_name: row.user_name,
        phone: row.phone,
        parent_phone: row.parent_phone,
        score: row.score,
      })),
    [rows]
  );

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Center exam marks report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              View and export scores for a manual center exam.
            </Text>
          </Box>
          <Flex align="flex-start" gap={3} flexWrap="wrap" justifyContent={{ base: "flex-start", md: "flex-end" }}>
            <Box minW={{ base: "100%", md: "280px" }} maxW={{ base: "100%", md: "420px" }}>
              <ReactSelect
                options={examOptions}
                placeholder="Select exam"
                styles={selectStyles}
                value={examOptions.find((o) => o.value === String(examId)) || null}
                onChange={(opt) => setExamId(opt?.value ? String(opt.value) : "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                isClearable
              />
            </Box>
            <LogoutMenu />
          </Flex>
        </Flex>

        <Flex gap={4} mb={6} flexWrap="wrap" align="flex-end" justify="space-between">
          <Box flex="1" minW={{ base: "100%", md: "320px" }} maxW="420px">
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Search
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <RiSearch2Fill color="var(--text-muted)" />
              </InputLeftElement>
              <Input
                placeholder="Name, code, or phone…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="14px"
                borderColor="var(--border-subtle)"
                bg="var(--surface-muted)"
                color="var(--text-primary)"
                fontWeight="600"
                isDisabled={!examId}
                _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
              />
            </InputGroup>
          </Box>

          {examId && rows.length > 0 ? (
            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={`center-exam-marks-${examId}-${dayjs().format("YYYY-MM-DD")}.csv`}
            >
              <Button
                size="sm"
                borderRadius="lg"
                bg={brandPalette.secondary}
                color="white"
                fontWeight="800"
                px={6}
                _hover={{ bg: brandPalette.primary }}
              >
                Export CSV
              </Button>
            </CSVLink>
          ) : null}
        </Flex>

        <Box
          border="1px solid"
          borderColor="var(--border-subtle)"
          bg="var(--surface-raised)"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.12)"
          overflowX="auto"
          color="var(--text-primary)"
        >
          {!examId ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Select an exam to load marks.
            </Text>
          ) : loading && rows.length === 0 ? (
            <Text fontWeight="600" color="var(--text-primary)">
              Loading marks…
            </Text>
          ) : rows.length === 0 ? (
            <Text fontWeight="600" color="var(--text-muted)">
              No marks found for this exam.
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th color="var(--text-primary)">#</Th>
                  <Th color="var(--text-primary)">Student</Th>
                  <Th color="var(--text-primary)">Code</Th>
                  <Th color="var(--text-primary)">Phone</Th>
                  <Th color="var(--text-primary)">Parent phone</Th>
                  <Th color="var(--text-primary)">Score</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => (
                  <Tr key={row.key}>
                    <Td fontWeight="700">#{row.index}</Td>
                    <Td fontWeight="600">{row.name}</Td>
                    <Td>{row.user_name}</Td>
                    <Td>{row.phone}</Td>
                    <Td>{row.parent_phone}</Td>
                    <Td
                      fontWeight="700"
                      color={
                        row.score === "Not graded"
                          ? "var(--text-muted)"
                          : brandPalette.primary
                      }
                    >
                      {row.score}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default ManualExamMarksReport;
